import { useNavigation, useDelete } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Button, Space, Popconfirm, Switch, message, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';

interface FAQ {
  id: number;
  questionUz: string;
  answerUz: string;
  category: string | null;
  sortOrder: number;
  isActive: boolean;
}

export const FaqList = () => {
  const { edit, create } = useNavigation();
  const { mutate: deleteFaq } = useDelete();

  const { tableProps, tableQueryResult } = useTable<FAQ>({
    resource: 'admin/faqs',
    syncWithLocation: true,
    sorters: {
      initial: [{ field: 'sortOrder', order: 'asc' }],
    },
  });

  const handleToggle = async (id: number, currentValue: boolean) => {
    try {
      await apiClient.put(`/admin/faqs/${id}`, { isActive: !currentValue });
      message.success(currentValue ? 'FAQ yashirildi' : 'FAQ faollashtirildi');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleReorder = async (id: number, newOrder: number) => {
    try {
      await apiClient.put(`/admin/faqs/${id}`, { sortOrder: newOrder });
      message.success('Tartib o\'zgartirildi');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const moveUp = async (record: FAQ) => {
    const currentData = tableProps.dataSource as FAQ[] | undefined;
    if (!currentData) return;

    const currentIndex = currentData.findIndex(item => item.id === record.id);
    if (currentIndex <= 0) return;

    const prevItem = currentData[currentIndex - 1];

    try {
      await Promise.all([
        apiClient.put(`/admin/faqs/${record.id}`, { sortOrder: prevItem.sortOrder }),
        apiClient.put(`/admin/faqs/${prevItem.id}`, { sortOrder: record.sortOrder }),
      ]);
      message.success('Tartib o\'zgartirildi');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const moveDown = async (record: FAQ) => {
    const currentData = tableProps.dataSource as FAQ[] | undefined;
    if (!currentData) return;

    const currentIndex = currentData.findIndex(item => item.id === record.id);
    if (currentIndex >= currentData.length - 1) return;

    const nextItem = currentData[currentIndex + 1];

    try {
      await Promise.all([
        apiClient.put(`/admin/faqs/${record.id}`, { sortOrder: nextItem.sortOrder }),
        apiClient.put(`/admin/faqs/${nextItem.id}`, { sortOrder: record.sortOrder }),
      ]);
      message.success('Tartib o\'zgartirildi');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const columns = [
    {
      title: 'Tartib',
      key: 'reorder',
      width: 100,
      render: (_: unknown, record: FAQ) => {
        const currentData = tableProps.dataSource as FAQ[] | undefined;
        const currentIndex = currentData?.findIndex(item => item.id === record.id) ?? -1;
        const isFirst = currentIndex === 0;
        const isLast = currentIndex === (currentData?.length ?? 0) - 1;

        return (
          <Space size="small">
            <Tooltip title="Yuqoriga ko'tarish">
              <Button
                type="text"
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={isFirst}
                onClick={() => moveUp(record)}
              />
            </Tooltip>
            <Tooltip title="Pastga tushirish">
              <Button
                type="text"
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={isLast}
                onClick={() => moveDown(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Savol',
      dataIndex: 'questionUz',
      key: 'questionUz',
      ellipsis: true,
    },
    {
      title: 'Kategoriya',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category: string | null) => category || '-',
    },
    {
      title: 'Holat',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (active: boolean, record: FAQ) => (
        <Switch
          checked={active}
          onChange={() => handleToggle(record.id, active)}
          checkedChildren="Faol"
          unCheckedChildren="Yashirin"
          size="small"
        />
      ),
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: FAQ) => (
        <Space>
          <Tooltip title="Tahrirlash">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => edit('admin/faqs', record.id)}
            />
          </Tooltip>
          <Popconfirm
            title="O'chirishni tasdiqlaysizmi?"
            onConfirm={() =>
              deleteFaq({
                resource: 'admin/faqs',
                id: record.id,
              })
            }
          >
            <Tooltip title="O'chirish">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <List
      title="FAQ"
      headerButtons={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => create('admin/faqs')}
        >
          Yangi FAQ
        </Button>
      }
    >
      <Table {...tableProps} columns={columns} rowKey="id" />
    </List>
  );
};
